import { useParams } from 'react-router'
import React, { useRef, useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, Grid, IconButton, Paper, Skeleton, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import {Edit,  Restore as RestoreIcon } from '@mui/icons-material'
import { enqueueSnackbar } from 'notistack'
import { fieldLabelSx, sectionTitleRowSx, toggleGroupSx } from '.'
import RevisionHistory from '../../../shared_elements/components/RevisionHistory'
import gatelesisLogo from '../../assets/img/gatelesis_logo_blue.svg'
import { getLesseeApi, getOwnerListApi } from '../../../shared_elements/actionCreators'
import { getInvoiceHeaderConfigApi, partiesApi, updateInvoiceHeaderLogoApi, updateInvoiceHeaderAllSectionsApi } from '../apiService'
import { globalGetService, globalPutService, globalPostFileUploadService, globalPostFileUpdateService } from '../../../utils/globalApiServices'
import { checkApiStatus, getGlobalCookie } from '../../../utils'
import appConfig from '../../../config'
import fileUpload, { getApiResponse } from '../../../utils/fileUpload'
import FileUploadLayout from '../../../shared_elements/components/FileUploadLayout'
import { DeletePopUp } from '../../../shared_elements'
import { trackActivity } from '../../../utils/mixpanel'

const INVOICE_TYPE_OPTIONS = [
  { label: 'Reserve', value: 1 },
  { label: 'Rental', value: 2 },
  { label: 'Penalty', value: 3 },
  { label: 'Debit', value: 4 },
  { label: 'Credit', value: 5 },
  { label: 'Adhoc', value: 9 },
  { label: 'Security Deposit', value: 6 },
  { label: 'Management Fee', value: 10 },
]
// ── Defaults (field names match API) ──
const makeDefaultSection = (s = {}) => ({
  slug: s.slug || null,
  is_active: s.is_active ?? false,
  position: s.position || null,
  data_source: s.data_source || 'manual_entry',
  data_source_id: s.data_source_id || null,
  name: s.name || '',
  address: s.address || '',
  applicable_invoice_types: s.applicable_invoice_types?.length ? s.applicable_invoice_types : INVOICE_TYPE_OPTIONS.map(o => o.value),
  section_title: s.section_title || 'Beneficiary Owner',
  autoFillLocked: false,
  snapshots: {},
})

const makeDefaultConfig = () => ({
  slug: null,
  logo_position: null,
  logo_download_url: null,
  logo_file_name: 'GATelesis Logo',
  header1: makeDefaultSection(),
  header2: makeDefaultSection(),
  body: makeDefaultSection(),
})
// ── Helpers ──
const parseInvoiceHeaderConfig = (data) => ({
  slug: data.slug,
  logo_position: data.logo_position || null,
  logo_download_url: data.logo_download_url || null,
  logo_file_name: data.logo_file_name || 'GATelesis Logo',
  logo_attachment_slug: data.logo_attachment_slug,
  header1: makeDefaultSection(data.sections?.header1 || {}),
  header2: makeDefaultSection(data.sections?.header2 || {}),
  body: makeDefaultSection(data.sections?.body || {}),
})

const buildSectionPayload = (sectionData, isBody) => ({
  is_active: sectionData.is_active,
  data_source: sectionData.data_source,
  data_source_id: sectionData.data_source_id || null,
  name: sectionData.name || null,
  address: sectionData.address || null,
  applicable_invoice_types: sectionData.applicable_invoice_types,
  ...(isBody ? { section_title: sectionData.section_title } : { position: sectionData.position }),
})

const validateSection = (sectionData, isBody = false) => {
  const errors = {}
  if (sectionData.is_active) {
    if (!sectionData.name?.trim()) errors.name = true
    if (!sectionData.applicable_invoice_types?.length) errors.applicable_invoice_types = true
    if (!isBody && !sectionData.position) errors.position = true
  }
  return errors
}

const getContactOptions = (data_source, lesseeOptions, ownerOptions, partyOptions) => {
  if (data_source === 'lessee_config') return lesseeOptions
  if (data_source === 'owner_lessor_config') return ownerOptions
  if (data_source === 'parties') return partyOptions
  return []
}

const getContactMap = (data_source, lesseeContactMap, ownerContactMap, partyContactMap) => {
  if (data_source === 'lessee_config') return lesseeContactMap
  if (data_source === 'owner_lessor_config') return ownerContactMap
  if (data_source === 'parties') return partyContactMap
  return {}
}
// ── Sub-components ──

function InvoiceTypeChips({ value, onChange, disabled }) {
  const toggle = (id) => {
    if (disabled) return
    onChange(value.includes(id) ? value.filter(t => t !== id) : [...value, id])
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
      {INVOICE_TYPE_OPTIONS.map(({ value: id, label }) => {
        const active = value.includes(id)
        return (
          <Chip
            key={id} label={label} size="small" onClick={() => toggle(id)}
            sx={{
              cursor: disabled ? 'default' : 'pointer', borderRadius: '4px', height: 24, fontSize: 11,
              fontWeight: active ? 600 : 400, bgcolor: active ? 'primary.main' : 'grey.100',
              color: active ? 'white' : 'text.secondary', border: '1px solid',
              borderColor: active ? 'primary.main' : 'grey.300',
              '&:hover': { bgcolor: active ? 'primary.main' : 'grey.100', borderColor: 'primary.main' },
              '&.MuiChip-root': { borderRadius: '4px' },
            }}
          />
        )
      })}
    </Box>
  )
}

function DataSourceFields({ data_source, data_source_id, contactOptions = [], onSourceChange, onContactChange, autoFillLocked, onEditAutoFill, disabled }) {
  const selectedContact = contactOptions.find(o => o.value === data_source_id) || null
  return (
    <Box>
      <Typography sx={fieldLabelSx}>
        Data Source&nbsp;
        <Box component="span" sx={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'text.disabled' }}>
          (select source or enter manually below)
        </Box>
      </Typography>
      <ToggleButtonGroup value={data_source} exclusive size="small" disabled={disabled} onChange={(_, v) => { if (v) onSourceChange(v) }} sx={toggleGroupSx}>
        <ToggleButton value="manual_entry">Manual Entry</ToggleButton>
        <ToggleButton value="parties">Party Config</ToggleButton>
        <ToggleButton value="owner_lessor_config">Owner / Lessor Config</ToggleButton>
        <ToggleButton value="lessee_config">Lessee Config</ToggleButton>
      </ToggleButtonGroup>
      {data_source !== 'manual_entry' && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Autocomplete
            size="small" options={contactOptions} value={selectedContact}
            getOptionLabel={(o) => o?.label || ''} isOptionEqualToValue={(o, v) => o?.value === v?.value}
            onChange={(_, v) => onContactChange(v)}
            // disabled={autoFillLocked || !!disabled}
            disableClearable={true}
            sx={{ flex: 1 }}
            renderInput={(p) => <TextField {...p} placeholder="— Select a contact —" size="small" />}
          />
          {autoFillLocked && selectedContact && (
            <Tooltip title="Edit auto-filled data">
              <IconButton size="small" color="primary" onClick={onEditAutoFill} sx={{ flexShrink: 0 }}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  )
}

function HeaderCard({ sectionLabel, title, tooltip, data, onChange, onToggle, blockedPositions, onRevisionHistory, errors = {}, saving = false, lesseeOptions, lesseeContactMap, ownerOptions, ownerContactMap, partyOptions, partyContactMap }) {
  const { is_active, position, data_source, data_source_id, name, address, applicable_invoice_types, autoFillLocked } = data
  const contactOptions = getContactOptions(data_source, lesseeOptions, ownerOptions, partyOptions)
  const contactMap = getContactMap(data_source, lesseeContactMap, ownerContactMap, partyContactMap)

  const handleSourceChange = (newSource) => {
    const updatedSnapshots = { ...(data.snapshots || {}), [data_source]: { data_source_id, name, address, autoFillLocked } }
    const restored = updatedSnapshots[newSource] || { data_source_id: null, name: '', address: '', autoFillLocked: false }
    onChange({ data_source: newSource, snapshots: updatedSnapshots, ...restored })
  }

  const handleContactChange = (option) => {
    if (option?.value) {
      const lookup = contactMap[option.value]
      if (lookup) { onChange({ data_source_id: option.value, name: lookup.name, address: lookup.address, autoFillLocked: true }); return }
    }
    onChange({ data_source_id: option?.value || null, autoFillLocked: false })
  }

  return (
    <Paper elevation={0} sx={{ boxShadow: '0px 0px 2px 1px #BBBBBB', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={sectionTitleRowSx}>
        <Typography variant="p">{sectionLabel}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onRevisionHistory && (
            <Tooltip title="Revision History">
              <RestoreIcon className="revision-history" onClick={onRevisionHistory} fontSize="small" color="primary" sx={{ cursor: 'pointer' }} />
            </Tooltip>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Switch size="small" checked={is_active} onChange={(e) => onToggle(e.target.checked)} />
        <Typography sx={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{title}</Typography>
        <Tooltip title={tooltip} placement="top">
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'pointer' }} />
        </Tooltip>
        <Typography variant="caption" sx={{ color: is_active ? 'success.main' : 'text.disabled', fontWeight: is_active ? 600 : 400, fontSize: 11 }}>
          {is_active ? 'Active' : 'Not Active'}
        </Typography>
      </Box>

      {saving ? (
        <Box sx={{ p: 2, flex: 1 }}>
          {[32, 36, 36, 80].map((h, i) => <Skeleton key={i} variant="rounded" width="100%" height={h} sx={{ mb: 1.5 }} />)}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {[1, 2, 3, 4, 5, 6].map(j => <Skeleton key={j} variant="rounded" width={68} height={24} />)}
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 2, flex: 1, opacity: is_active ? 1 : 0.45, pointerEvents: is_active ? 'auto' : 'none', minWidth: 0 }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ ...fieldLabelSx, color: errors.position && is_active ? 'error.main' : 'text.secondary' }}>
              Position in Invoice Header&nbsp;<Box component="span" sx={{ color: 'error.main' }}>*</Box>
            </Typography>
            <ToggleButtonGroup
              value={position} exclusive size="small"
              onChange={(_, v) => { if (v) onChange({ position: v }) }}
              sx={{ ...toggleGroupSx, ...(errors.position && is_active ? { outline: '1px solid', outlineColor: 'error.main', borderRadius: 1 } : {}) }}
            >
              {['left', 'center', 'right'].map(pos => (
                <ToggleButton key={pos} value={pos} disabled={blockedPositions.has(pos) && position !== pos}>
                  {pos === 'left' ? '← Left' : pos === 'center' ? 'Center' : 'Right →'}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {errors.position && is_active && (
              <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>
                {errors.position === 'conflict_logo'
                  ? 'Position conflicts with Logo Position'
                  : errors.position === 'conflict_header1'
                  ? 'Position conflicts with Header-1'
                  : errors.position === 'conflict_header2'
                  ? 'Position conflicts with Header-2'
                  : 'Position is required'}
              </Typography>
            )}
          </Box>
          <Box sx={{ mb: 1.5 }}>
            <DataSourceFields
              data_source={data_source} data_source_id={data_source_id} contactOptions={contactOptions}
              onSourceChange={handleSourceChange} onContactChange={handleContactChange}
              autoFillLocked={autoFillLocked} onEditAutoFill={() => onChange({ autoFillLocked: false })}
            />
          </Box>
          <TextField
            label="Name" required size="small" fullWidth value={name}
            onChange={(e) => onChange({ name: e.target.value })}
            error={!!(errors.name && is_active)}
            helperText={errors.name && is_active ? 'Name is required' : `${name.length}/200`}
            disabled={autoFillLocked}
            sx={{ mb: 1.5 }}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 200 }, formHelperText: { sx: { textAlign: 'right', mr: 0 } } }}
            placeholder="Enter name"
          />
          <TextField
            label="Contact Address" size="small" fullWidth multiline minRows={3} value={address}
            onChange={(e) => onChange({ address: e.target.value })}
            disabled={autoFillLocked}
            helperText={`${address.length}/500`}
            sx={{ mb: 1.5 }}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 500 }, formHelperText: { sx: { textAlign: 'right', mr: 0 } } }}
            placeholder="Enter address"
          />
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ ...fieldLabelSx, color: errors.applicable_invoice_types && is_active ? 'error.main' : 'text.secondary' }}>
            Applicable Invoice Types&nbsp;<Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <InvoiceTypeChips value={applicable_invoice_types} onChange={(v) => onChange({ applicable_invoice_types: v })} />
          {errors.applicable_invoice_types && is_active && (
            <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>At least one invoice type is required</Typography>
          )}
        </Box>
      )}
    </Paper>
  )
}

function InvoiceHeaderSkeleton() {
  return (
    <>
      <Paper elevation={0} sx={{ boxShadow: '0px 0px 2px 1px #BBBBBB', mb: 1.5, overflow: 'hidden' }}>
        <Box sx={sectionTitleRowSx}><Skeleton width={100} height={20} /></Box>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ md: 6, xs: 12 }}><Skeleton width={180} height={16} sx={{ mb: 0.75 }} /><Skeleton width={200} height={36} sx={{ borderRadius: 1 }} /></Grid>
            <Grid size={{ md: 6, xs: 12 }}><Skeleton width={60} height={16} sx={{ mb: 0.75 }} /><Skeleton variant="rounded" width="100%" height={72} /></Grid>
          </Grid>
        </Box>
      </Paper>
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {[0, 1].map((i) => (
          <Grid key={i} size={{ md: 6, xs: 12 }}>
            <Paper elevation={0} sx={{ boxShadow: '0px 0px 2px 1px #BBBBBB', overflow: 'hidden', height: '100%' }}>
              <Box sx={sectionTitleRowSx}><Skeleton width={80} height={20} /></Box>
              <Box sx={{ p: 2 }}>
                {[32, 36, 36, 36, 80].map((h, j) => <Skeleton key={j} variant="rounded" width="100%" height={h} sx={{ mb: 1.5 }} />)}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {[1, 2, 3, 4, 5, 6].map(j => <Skeleton key={j} variant="rounded" width={68} height={24} />)}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Paper elevation={0} sx={{ boxShadow: '0px 0px 2px 1px #BBBBBB', overflow: 'hidden' }}>
        <Box sx={sectionTitleRowSx}><Skeleton width={220} height={20} /></Box>
        <Box sx={{ p: 2 }}>
          <Skeleton width={340} height={32} sx={{ mb: 1.5 }} />
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            <Grid size={{ md: 6, xs: 12 }}><Skeleton variant="rounded" width="100%" height={36} /></Grid>
          </Grid>
          <Skeleton variant="rounded" width="100%" height={36} sx={{ mb: 1.5 }} />
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            <Grid size={{ md: 6, xs: 12 }}><Skeleton variant="rounded" width="100%" height={36} /></Grid>
            <Grid size={{ md: 6, xs: 12 }}><Skeleton variant="rounded" width="100%" height={80} /></Grid>
          </Grid>
          <Skeleton width={170} height={16} sx={{ mb: 0.75 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {[1, 2, 3, 4, 5, 6].map(j => <Skeleton key={j} variant="rounded" width={68} height={24} />)}
          </Box>
        </Box>
      </Paper>
    </>
  )
}
// ── Main component ──
const InvoiceHeaderConfig = () => {
  const params = useParams()
  const dispatch = useDispatch()
  const { leaseBasicDetails } = useSelector((state) => state.leases);
  const isFeatureDisabled = appConfig.customReportTemplateOwnerIds.includes(leaseBasicDetails?.lessor?.id)
  const { lessee, owner } = useSelector((state) => state.leases)
  const fileInputRef = useRef()
  const [logoFile, setLogoFile] = useState(null)
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null)
  const [config, setConfig] = useState(makeDefaultConfig())
  const [savedConfig, setSavedConfig] = useState(makeDefaultConfig())
  const [parties, setParties] = useState([])
  const [isLoading, setLoading] = useState({ skeletonLoader: false, buttonLoader: false })
  const [sectionSaving, setSectionSaving] = useState({ logo: false, header1: false, header2: false, body: false })
  const [errors, setErrors] = useState({ logo_position: false, header1: {}, header2: {}, body: {} })
  const [uploadFileProgressData, setUploadFileProgressData] = useState({})
  const [revisionHistory, setRevisionHistory] = useState({ modal: false, mode: '', data: null, section: null })
  const [replaceLogoModal, setReplaceLogoModal] = useState({ modal: false })
  const [deleteAttachment, setDeleteAttachment] = useState({ modal: false, data: null });
  const [uploadFileStatus, setUploadFileStatus] = useState({ mode: '', modal: false, data: null })
  const [logoSizeError, setLogoSizeError] = useState('')
  const { slug, logo_position, logo_download_url, logo_file_name, header1, header2, body } = config
  const blockedPositions = (other) => new Set([...(logo_position ? [logo_position] : []), ...(other.is_active && other.position ? [other.position] : []),])
  const h1Blocked = blockedPositions(header2)
  const h2Blocked = blockedPositions(header1)
  const buildOptionsAndMap = (list) => {
    const items = list || []
    return {
      options: items.map(item => ({ value: item.id, label: item.name })),
      contactMap: Object.fromEntries(items.map(item => [item.id, { name: item.name, address: item.contact_address || '' }])),
    }
  }
  const { options: lesseeOptions, contactMap: lesseeContactMap } = buildOptionsAndMap(lessee)
  const { options: ownerOptions, contactMap: ownerContactMap } = buildOptionsAndMap(owner)
  const { options: partyOptions, contactMap: partyContactMap } = buildOptionsAndMap(parties)
  const sharedContactProps = { lesseeOptions, lesseeContactMap, ownerOptions, ownerContactMap, partyOptions, partyContactMap }

  const isSectionChanged = (curr, saved, isBody) => {
    const fields = ['is_active', 'data_source', 'data_source_id', 'name', 'address']
    if (isBody) fields.push('section_title')
    else fields.push('position')
    if (fields.some(f => (curr[f] ?? null) !== (saved[f] ?? null))) return true
    const a = [...(curr.applicable_invoice_types || [])].sort((x, y) => x - y)
    const b = [...(saved.applicable_invoice_types || [])].sort((x, y) => x - y)
    return JSON.stringify(a) !== JSON.stringify(b)
  }
  const hasChanges = !!logoFile ||
    config.logo_position !== savedConfig.logo_position ||
    isSectionChanged(config.header1, savedConfig.header1, false) ||
    isSectionChanged(config.header2, savedConfig.header2, false) ||
    isSectionChanged(config.body, savedConfig.body, true)

  const fetchConfig = useCallback(() => {
    setLoading(prev => ({ ...prev, skeletonLoader: true }))
    globalGetService(getInvoiceHeaderConfigApi(params)).then((response) => {
      if (checkApiStatus(response)) {
        const parsed = parseInvoiceHeaderConfig(response?.data?.data)
        setConfig(parsed)
        setSavedConfig(parsed)
      }
      setLoading(prev => ({ ...prev, skeletonLoader: false }))
    })
  }, [params])

  const fetchParties = useCallback(() => {
    globalGetService(partiesApi(params)).then(response => {
      if (checkApiStatus(response)) setParties(response?.data?.data || [])
    })
  }, [params])

  useEffect(() => {
    if (!lessee?.length) dispatch(getLesseeApi())
    if (!owner?.length) dispatch(getOwnerListApi())
  }, [dispatch])

  useEffect(() => {
    fetchParties()
  }, [fetchParties])

  useEffect(() => {
    setUploadFileProgressData({ uploadedFileData: getApiResponse('Parties', null) })
    fetchConfig()
  }, [fetchConfig])

  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: 'Invoice Header Configuration', page_type })
  }

  const onSectionChange = (section, changes) => {
    setConfig(prev => ({ ...prev, [section]: { ...prev[section], ...changes } }))
    const clearedErrors = Object.keys(changes).reduce((acc, key) => {
      if (errors[section]?.[key]) acc[key] = false
      return acc
    }, {})
    if (Object.keys(clearedErrors).length) setErrors(prev => ({ ...prev, [section]: { ...prev[section], ...clearedErrors } }))
  }

  const handleSaveAll = async () => {
    const logoError = !logo_position
    const h1Errors = validateSection(config.header1, false)
    const h2Errors = validateSection(config.header2, false)
    const bodyErrors = validateSection(config.body, true)

    if (config.header1.is_active && config.header1.position) {
      if (config.header1.position === logo_position) h1Errors.position = 'conflict_logo'
      else if (config.header2.is_active && config.header1.position === config.header2.position) h1Errors.position = 'conflict_header2'
    }
    if (config.header2.is_active && config.header2.position) {
      if (config.header2.position === logo_position) h2Errors.position = 'conflict_logo'
      else if (config.header1.is_active && config.header2.position === config.header1.position) h2Errors.position = 'conflict_header1'
    }

    setErrors({ logo_position: logoError, header1: h1Errors, header2: h2Errors, body: bodyErrors })
    if (logoError || Object.keys(h1Errors).length || Object.keys(h2Errors).length || Object.keys(bodyErrors).length) return

    const payload = {
      logo_position,
      body: buildSectionPayload(config.body, true),
      header1: buildSectionPayload(config.header1, false),
      header2: buildSectionPayload(config.header2, false),
    }

    setSectionSaving({ logo: true, header1: true, header2: true, body: true })

    const response = await globalPutService(updateInvoiceHeaderAllSectionsApi(params), payload)

    setSectionSaving({ logo: false, header1: false, header2: false, body: false })

    if (checkApiStatus(response)) {
      setSavedConfig(prev => ({ ...prev, logo_position, header1: { ...config.header1 }, header2: { ...config.header2 }, body: { ...config.body } }))
      trackActivity('Item Edited', { page_title: `Edit Invoice Header Configuration`, success_msg: response?.data?.message, payload, response: response?.data })
      enqueueSnackbar('Configuration saved successfully', { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
    } else {
      trackActivity('Item Edited', { page_title: `Edit Invoice Header Configuration`, error_msg: response?.data?.message, payload, response: response?.data })
      enqueueSnackbar('Failed to save. Please try again.', { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
    }
  }

  const handleCancelAll = () => {
    setConfig(savedConfig)
    setErrors({ logo_position: false, header1: {}, header2: {}, body: {} })
    setLogoFile(null)
    setLogoPreviewUrl(null)
    setLogoSizeError('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleSectionActiveChange = (section, enabled) => {
    const isHeader = section !== 'body'
    const positionReset = !enabled && isHeader ? { position: null } : {}
    setConfig(prev => ({ ...prev, [section]: { ...prev[section], is_active: enabled, ...positionReset } }))
  }
  // ── Logo handlers ──
  const handleLogoPosChange = (newPos) => {
    if (!newPos || newPos === logo_position) return
    setConfig(prev => ({ ...prev, logo_position: newPos }))
    setErrors(prev => ({ ...prev, logo_position: false }))
  }

  const handleLogoUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (savedConfig.logo_attachment_slug && file.name === savedConfig.logo_file_name) {
      setLogoSizeError('This logo is already uploaded. Please choose a different file.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoSizeError('Logo file size exceeds 2MB. Please upload a smaller file.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    setLogoSizeError('')
    setLogoFile(file)
    setReplaceLogoModal({ modal: true })
  }
  const handleLogoReplace = () => {
    setSectionSaving(prev => ({ ...prev, logo: true }))

    const proceedWithUpdate = (attachmentSlug) => {
      globalPutService(updateInvoiceHeaderLogoApi(params), { logo_position: savedConfig.logo_position, attachment_slug: attachmentSlug }).then(response => {
        setSectionSaving(prev => ({ ...prev, logo: false }))
        if (checkApiStatus(response)) {
          setLogoFile(null)
          setLogoPreviewUrl(null)
          if (fileInputRef.current) fileInputRef.current.value = ''
          setReplaceLogoModal({ modal: false })
          const { logo_download_url: newUrl, logo_file_name: newFileName, logo_attachment_slug: newSlug } = response.data.data
          setConfig(prev => ({ ...prev, logo_download_url: newUrl || prev.logo_download_url, logo_file_name: newFileName || prev.logo_file_name, logo_attachment_slug: newSlug || prev.logo_attachment_slug }))
          setSavedConfig(prev => ({ ...prev, logo_download_url: newUrl || prev.logo_download_url, logo_file_name: newFileName || prev.logo_file_name, logo_attachment_slug: newSlug || prev.logo_attachment_slug }))
          enqueueSnackbar("Logo uploaded successfully", { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
          trackActivity('Item Edited', { page_title: `Edit Invoice Header Logo`, success_msg: 'Logo uploaded successfully', response: response?.data })
        } else {
          enqueueSnackbar(response.data.message, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
          trackActivity('Item Edited', { page_title: `Edit Invoice Header Logo`, error_msg: response.data.message, response: response?.data })

        }
      })
    }

    handleUpload('updated').then(upload_response => {
      if (upload_response.slug) {
        proceedWithUpdate(upload_response.slug)
        return
      }
      setSectionSaving(prev => ({ ...prev, logo: false }))
      setLogoFile(null)
      setLogoPreviewUrl(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      setReplaceLogoModal({ modal: false })
      enqueueSnackbar(upload_response.message, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
    })
  }

  const onDeleteLogo = () => {
    setLoading(prev => ({ ...prev, buttonLoader: true }))
    globalPutService(updateInvoiceHeaderLogoApi(params), { logo_position, logo_attachment_slug: null, logo: null, logo_download_url: null, logo_file_name: null }).then(updateResponse => {
      setLoading(prev => ({ ...prev, buttonLoader: false }))
      if (checkApiStatus(updateResponse)) {
        setDeleteAttachment({ modal: false, data: null })
        setConfig(prev => ({ ...prev, logo_download_url: null, logo_file_name: 'GATelesis Logo', logo_attachment_slug: null }))
        setSavedConfig(prev => ({ ...prev, logo_download_url: null, logo_file_name: 'GATelesis Logo', logo_attachment_slug: null }))
        setLogoFile(null)
        setLogoPreviewUrl(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
      }
      enqueueSnackbar(checkApiStatus(updateResponse) ? "Logo deleted successfully" : updateResponse.data.message, { variant: checkApiStatus(updateResponse) ? 'success' : 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } })
    })
  }

  const bodyContactOptions = getContactOptions(body.data_source, lesseeOptions, ownerOptions, partyOptions)
  const bodyContactMap = getContactMap(body.data_source, lesseeContactMap, ownerContactMap, partyContactMap)

  const handleBodyContactChange = (option) => {
    if (option?.value) {
      const lookup = bodyContactMap[option.value]
      if (lookup) { onSectionChange('body', { data_source_id: option.value, name: lookup.name, address: lookup.address, autoFillLocked: true }); return }
    }
    onSectionChange('body', { data_source_id: option?.value || null, autoFillLocked: false })
  }

  const handleUpload = async (mode) => {
    const result = { success: false, slug: null, message: '' }
    if (!logoFile) return result
    const filePayload = { files: [{ file_name: logoFile.name, file_size: logoFile.size, file_type: logoFile.name.split('.').pop().toLowerCase() }] }
    try {
      const preSignedRes = await globalPostFileUploadService(`/get-pre-signed-url/invoice_header_config/${slug}?record_action_type=${mode}`, filePayload)
      const successfulFiles = preSignedRes?.data?.data?.successful_files || []
      const failedFiles = preSignedRes?.data?.data?.failed_files || []
      if (failedFiles.length > 0 && failedFiles[0]?.attachment_slug) {
        result.slug = failedFiles[0].attachment_slug
        result.message = preSignedRes?.data?.message || ''
        return result
      }
      if (!successfulFiles.length) {
        result.message = preSignedRes?.data?.message || 'Upload failed'
        return result
      }
      const progressRefs = { current: [] }
      setUploadFileProgressData({ progressRefs, attachments: [logoFile], response: preSignedRes })
      await Promise.all(successfulFiles.map((fileData, index) =>
        globalPostFileUpdateService(fileData.pre_signed_url, logoFile, (progress) => {
          if (typeof progressRefs?.current?.[index] === 'function') progressRefs.current[index](progress)
        }).then(() =>
          globalPostFileUploadService(`/update-attachment-metadata/invoice_header_config/${slug}`, { attachment_slug: fileData.attachment_slug, status: { value: 1 } })
        )
      ))
      result.success = true
      result.slug = successfulFiles[0].attachment_slug
      result.message = preSignedRes?.data?.message || 'Attachments Uploaded Successfully.'
    } catch (err) {
      result.message = err?.message || 'Error while uploading files'
    }
    return result
  }

  return (
    <>
      <Paper elevation={0} sx={{ marginTop: '8px', padding: '10px', boxShadow: '0px 0px 2px 1px #BBBBBB', overflow: 'hidden', minWidth: 0 }}>
        <Grid sx={{ marginBottom: '10px' }}>
          <Typography variant="p">Invoice Header Configuration</Typography>
        </Grid>
        <Box sx={{ px: 1.5, py: 1, bgcolor: isFeatureDisabled ? '#fef2f2' : '#fffbeb', border: '1px solid', borderColor: isFeatureDisabled ? '#fecaca' : '#fde68a', borderRadius: 1, display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: isFeatureDisabled ? '#ef4444' : '#f59e0b', flexShrink: 0, mt: 0.25 }} />
          <Typography sx={{ fontSize: 12, color: isFeatureDisabled ? '#991b1b' : '#78350f' }}>
            {isFeatureDisabled
              ? `This feature is not applicable for the ${leaseBasicDetails?.lessor?.name} lessor.`
              : <>Changes made here will apply to <strong>future invoices only.</strong> Invoices already generated will not be modified.</>
            }
          </Typography>
        </Box>

        {isLoading.skeletonLoader ? <InvoiceHeaderSkeleton /> : <Box sx={{ opacity: isFeatureDisabled ? 0.5 : 1, pointerEvents: isFeatureDisabled ? 'none' : 'auto' }}><>
          {/* ── Logo Settings ── */}
          <Paper elevation={0} sx={{ boxShadow: '0px 0px 2px 1px #BBBBBB', mb: 1.5, overflow: 'hidden', mt: 0.25 }}>
            <Box sx={sectionTitleRowSx}>
              <Typography variant="p">Logo Settings</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Revision History">
                  <RestoreIcon className="revision-history" onClick={() => { setRevisionHistory({ modal: true, mode: '', data: { slug: config.slug }, section: 'logo' }); handleItemClicked('Revision History Icon - Logo Settings') }} fontSize="small" color="primary" sx={{ cursor: 'pointer' }} />
                </Tooltip>
              </Box>
            </Box>
            {sectionSaving.logo ? (
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ md: 6, xs: 12 }}><Skeleton width={180} height={16} sx={{ mb: 0.75 }} /><Skeleton width={200} height={36} sx={{ borderRadius: 1 }} /></Grid>
                  <Grid size={{ md: 6, xs: 12 }}><Skeleton width={60} height={16} sx={{ mb: 0.75 }} /><Skeleton variant="rounded" width="100%" height={72} /></Grid>
                </Grid>
              </Box>
            ) : (
              <Box sx={{ p: 2, minWidth: 0 }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid size={{ md: 6, xs: 12 }} sx={{ minWidth: 0 }}>
                    <Typography sx={{ ...fieldLabelSx, color: errors.logo_position ? 'error.main' : 'text.secondary' }}>
                      Logo Position in Header&nbsp;<Box component="span" sx={{ color: 'error.main' }}>*</Box>
                    </Typography>
                    <ToggleButtonGroup
                      value={logo_position} exclusive size="small" onChange={(_, v) => handleLogoPosChange(v)}
                      sx={{ ...toggleGroupSx, ...(errors.logo_position ? { outline: '1px solid', outlineColor: 'error.main', borderRadius: 1 } : {}) }}
                    >
                      {['left', 'right'].map(pos => (
                        <ToggleButton key={pos} value={pos} disabled={((header1.is_active && header1.position === pos) || (header2.is_active && header2.position === pos)) && logo_position !== pos}>{pos === 'left' ? '← Left' : 'Right →'}</ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    {errors.logo_position && <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>Position is required</Typography>}
                  </Grid>
                  <Grid size={{ md: 6, xs: 12 }} sx={{ minWidth: 0 }}>
                    <Typography sx={fieldLabelSx}>Logo</Typography>
                    <input type="file" ref={fileInputRef} accept=".png,.jpg,.jpeg" style={{ display: 'none' }} onChange={handleLogoUpload} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, bgcolor: 'grey.50', minHeight: 72, overflow: 'hidden' }}>
                      <Box sx={{ width: 90, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <img src={logoPreviewUrl || logo_download_url || gatelesisLogo} alt="Logo" style={{ maxHeight: 44, maxWidth: 88, objectFit: 'contain' }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{logoFile?.name || logo_file_name}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>PNG, JPG (max 2MB)</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" startIcon={<FileUploadOutlinedIcon sx={{ fontSize: '13px !important' }} />} onClick={() =>{ fileInputRef.current?.click(); handleItemClicked('Replace Logo Button')}} sx={{ fontSize: 11, textTransform: 'none', py: 0.5 }}>Replace</Button>
                        <Button size="small" variant="outlined" color="error" startIcon={isLoading.buttonLoader ? <CircularProgress size={12} color="error" /> : <DeleteOutlineIcon sx={{ fontSize: '13px !important' }} />} onClick={() => { setDeleteAttachment({ modal: true, data: config.slug }); setLogoSizeError(''); handleItemClicked('Remove Logo Button')  }} disabled={!logo_download_url || isLoading.buttonLoader} sx={{ fontSize: 11, textTransform: 'none', py: 0.5 }}>Remove</Button>
                      </Box>
                    </Box>
                    {logoSizeError && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75, fontSize: 11 }}>{logoSizeError}</Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>

          {/* ── Header-1 & Header-2 ── */}
          <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
            <Grid size={{ md: 6, xs: 12 }} sx={{ minWidth: 0 }}>
              <HeaderCard
                sectionLabel="Header-1" title="Header 1 — Primary"
                tooltip="Main header entry. Appears in the invoice header at the position you select."
                data={header1} onChange={(changes) => onSectionChange('header1', changes)}
                onToggle={(enabled) => handleSectionActiveChange('header1', enabled)} blockedPositions={h1Blocked}
                onRevisionHistory={() => { setRevisionHistory({ modal: true, mode: '', data: { slug: header1.slug }, section: 'header1' }); handleItemClicked('Revision History Icon - Header-1') }}
                errors={errors.header1} saving={sectionSaving.header1} {...sharedContactProps}
              />
            </Grid>
            <Grid size={{ md: 6, xs: 12 }} sx={{ minWidth: 0 }}>
              <HeaderCard
                sectionLabel="Header-2" title="Header 2 — Secondary"
                tooltip="Appears alongside Header 1 if both are enabled."
                data={header2} onChange={(changes) => onSectionChange('header2', changes)}
                onToggle={(enabled) => handleSectionActiveChange('header2', enabled)} blockedPositions={h2Blocked}
                onRevisionHistory={() => { setRevisionHistory({ modal: true, mode: '', data: { slug: header2.slug }, section: 'header2' }); handleItemClicked('Revision History Icon - Header-2') }}
                errors={errors.header2} saving={sectionSaving.header2} {...sharedContactProps}
              />
            </Grid>
          </Grid>

          {/* ── Invoice Body ── */}
          <Paper elevation={0} sx={{ boxShadow: '0px 0px 2px 1px #BBBBBB', overflow: 'hidden' }}>
            <Box sx={sectionTitleRowSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="p">Invoice Body — Additional Details</Typography>
                <Tooltip title="This section appears in the Invoice Body below Lessee Details, independent of the header configuration above." placement="top">
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'pointer' }} />
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Revision History">
                  <RestoreIcon className="revision-history" onClick={() => { setRevisionHistory({ modal: true, mode: '', data: { slug: body.slug }, section: 'body' }); handleItemClicked('Revision History Icon - Invoice Body') }} fontSize="small" color="primary" sx={{ cursor: 'pointer' }} />
                </Tooltip>
              </Box>
            </Box>
            {sectionSaving.body ? (
              <Box sx={{ p: 2 }}>
                <Skeleton width={340} height={32} sx={{ mb: 1.5 }} />
                <Grid container spacing={2} sx={{ mb: 1.5 }}>
                  <Grid size={{ md: 6, xs: 12 }}><Skeleton variant="rounded" width="100%" height={36} /></Grid>
                </Grid>
                <Skeleton variant="rounded" width="100%" height={36} sx={{ mb: 1.5 }} />
                <Grid container spacing={2} sx={{ mb: 1.5 }}>
                  <Grid size={{ md: 6, xs: 12 }}><Skeleton variant="rounded" width="100%" height={36} /></Grid>
                  <Grid size={{ md: 6, xs: 12 }}><Skeleton variant="rounded" width="100%" height={80} /></Grid>
                </Grid>
                <Skeleton width={170} height={16} sx={{ mb: 0.75 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {[1, 2, 3, 4, 5, 6].map(j => <Skeleton key={j} variant="rounded" width={68} height={24} />)}
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 2, minWidth: 0 }}>
                <FormControlLabel
                  sx={{ mb: 1.5, ml: 0 }}
                  control={<Checkbox size="small" sx={{ p: 0, mr: 0.75 }} checked={body.is_active} onChange={(e) => handleSectionActiveChange('body', e.target.checked)} />}
                  label={<Typography variant="body2">Show additional details section in <strong>Invoice Body</strong> (below Lessee Details)</Typography>}
                />
                <Box sx={{ opacity: body.is_active ? 1 : 0.4, pointerEvents: body.is_active ? 'auto' : 'none', minWidth: 0 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ md: 6, xs: 12 }} sx={{ minWidth: 0 }}>
                      <TextField
                        label="Section Title" size="small" fullWidth value={body.section_title}
                        onChange={(e) => onSectionChange('body', { section_title: e.target.value })}
                        sx={{ mb: 1.5 }} slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 100 } }}
                        placeholder="e.g. Beneficiary Owner"
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ mb: 1.5 }}>
                    <DataSourceFields
                      data_source={body.data_source} data_source_id={body.data_source_id} contactOptions={bodyContactOptions}
                      onSourceChange={(newSource) => {
                        const updatedSnapshots = { ...(body.snapshots || {}), [body.data_source]: { data_source_id: body.data_source_id, name: body.name, address: body.address, autoFillLocked: body.autoFillLocked } }
                        const restored = updatedSnapshots[newSource] || { data_source_id: null, name: '', address: '', autoFillLocked: false }
                        onSectionChange('body', { data_source: newSource, snapshots: updatedSnapshots, ...restored })
                      }}
                      onContactChange={handleBodyContactChange}
                      autoFillLocked={body.autoFillLocked} onEditAutoFill={() => onSectionChange('body', { autoFillLocked: false })}
                    />
                  </Box>
                  <Grid container spacing={2}>
                    <Grid size={{ md: 6, xs: 12 }} sx={{ minWidth: 0 }}>
                      <TextField
                        label="Name" required size="small" fullWidth value={body.name}
                        onChange={(e) => onSectionChange('body', { name: e.target.value })}
                        error={!!(errors.body?.name && body.is_active)}
                        helperText={errors.body?.name && body.is_active ? 'Name is required' : `${body.name.length}/200`}
                        disabled={body.autoFillLocked}
                        sx={{ mb: 1.5 }}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 200 }, formHelperText: { sx: { textAlign: 'right', mr: 0 } } }}
                        placeholder="Enter name"
                      />
                    </Grid>
                    <Grid size={{ md: 6, xs: 12 }} sx={{ minWidth: 0 }}>
                      <TextField
                        label="Address" size="small" fullWidth multiline minRows={3} value={body.address}
                        onChange={(e) => onSectionChange('body', { address: e.target.value })}
                        disabled={body.autoFillLocked}
                        helperText={`${body.address.length}/500`}
                        sx={{ mb: 1.5 }}
                        slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 500 }, formHelperText: { sx: { textAlign: 'right', mr: 0 } } }}
                        placeholder="Enter address"
                      />
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography sx={{ ...fieldLabelSx, color: errors.body?.applicable_invoice_types && body.is_active ? 'error.main' : 'text.secondary' }}>
                    Applicable Invoice Types&nbsp;<Box component="span" sx={{ color: 'error.main' }}>*</Box>
                  </Typography>
                  <InvoiceTypeChips value={body.applicable_invoice_types} onChange={(v) => onSectionChange('body', { applicable_invoice_types: v })} />
                  {errors.body?.applicable_invoice_types && body.is_active && (
                    <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>At least one invoice type is required</Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button className='cancel-btn' variant='outlined' size='small' onClick={() => { handleCancelAll(); handleItemClicked('Cancel Button - InvoiceHeaderConfig') }} disabled={!hasChanges || Object.values(sectionSaving).some(Boolean)}>Cancel</Button>
            <Button variant='contained' size='small' className='btn-primary' onClick={() => { handleSaveAll(); handleItemClicked('Save Button - InvoiceHeaderConfig') }} disabled={!hasChanges || Object.values(sectionSaving).some(Boolean)} startIcon={Object.values(sectionSaving).some(Boolean) ? <CircularProgress size={14} color="inherit" /> : null}>Save</Button>
          </Box>
        </></Box>}
      </Paper>

      {revisionHistory.modal && (
        <RevisionHistory
          moduleName={revisionHistory.section === 'logo' ? 'invoice_header_config' : 'invoice_header_section'}
          revisionHistory={revisionHistory}
          onToggleModal={() => setRevisionHistory({ modal: false, mode: '', data: null, section: null })}
        />
      )}
      {replaceLogoModal.modal &&
        <DeletePopUp
          modal={replaceLogoModal.modal}
          title="Replace Logo"
          content="Are you sure you want to replace the logo?"
          confirmText="Save"
          confirmColor="primary"
          onToggleModal={() => {
            setReplaceLogoModal({ modal: false })
            setLogoFile(null)
            setLogoPreviewUrl(null)
            setLogoSizeError('')
            if (fileInputRef.current) fileInputRef.current.value = ''
          }}
          onDeleteRecords={handleLogoReplace}
          circularloader={sectionSaving.logo}
        />
      }
      {deleteAttachment.modal ?
        <DeletePopUp
          modal={deleteAttachment.modal}
          title='Delete Logo'
          content={`Are you sure, you want to Delete?`}
          onToggleModal={() => setDeleteAttachment({ modal: false, data: null })}
          onDeleteRecords={onDeleteLogo}
          circularloader={isLoading?.buttonLoader}
        /> : null
      }
      <FileUploadLayout
        uploadFileStatus={uploadFileStatus} open={uploadFileStatus?.modal}
        setUploadFileStatus={setUploadFileStatus} uploadFileProgressData={uploadFileProgressData}
        progressRefs={uploadFileProgressData?.progressRefs} attachments={uploadFileProgressData?.attachments}
        onDownloadComplete={() => ({})}
      />
    </>
  )
}

export default InvoiceHeaderConfig
